import type { TimerSnapshot, UserSettings } from './types';
import { formatTimer, getTimerTone } from './format';

export interface MainElements {
  minutes: HTMLInputElement;
  seconds: HTMLInputElement;
  start: HTMLButtonElement;
  pause: HTMLButtonElement;
  reset: HTMLButtonElement;
  plusOne: HTMLButtonElement;
  plusFive: HTMLButtonElement;
  openPip: HTMLButtonElement;
  sound: HTMLInputElement;
  animations: HTMLInputElement;
  timer: HTMLElement;
  phase: HTMLElement;
  browserStatus: HTMLElement;
  pipStatus: HTMLElement;
  inputError: HTMLElement;
  pipMessage: HTMLElement;
}

export function renderMainView(root: HTMLElement, settings: UserSettings, pipSupported: boolean): MainElements {
  root.innerHTML = `
    <div class="app-shell">
      <header class="topbar">
        <a class="brand" href="/" aria-label="TeamsTimer Web 首頁">
          <span class="brand-mark" aria-hidden="true">
            <svg viewBox="0 0 28 28"><circle cx="14" cy="15" r="9"/><path d="M14 15V9m0 6 4 2.5M10 3h8"/></svg>
          </span>
          <span><strong>TeamsTimer</strong><small>WEB</small></span>
        </a>
        <div class="status-cluster" aria-label="系統狀態">
          <span class="status-chip ${pipSupported ? 'status-ok' : 'status-warn'}" id="browser-status">
            <span class="status-dot"></span>${pipSupported ? '瀏覽器支援置頂視窗' : '僅支援頁內計時'}
          </span>
          <span class="status-chip" id="pip-status"><span class="status-dot"></span>置頂視窗未開啟</span>
        </div>
      </header>

      <main class="workspace">
        <section class="hero-panel" aria-labelledby="timer-heading">
          <div class="eyebrow-row">
            <p class="eyebrow" id="timer-heading">會議計時</p>
            <span class="phase-badge" id="phase-label">尚未開始</span>
          </div>
          <div class="main-timer" id="main-timer" role="timer" aria-live="off" data-tone="green">${String(settings.minutes).padStart(2, '0')}:${String(settings.seconds).padStart(2, '0')}</div>
          <p class="timer-caption">切換頁籤或進入簡報後，計時仍會依實際經過時間校正。</p>
          <div class="quick-actions" aria-label="快速操作">
            <button class="pill-button" id="plus-one" type="button">＋1 分鐘</button>
            <button class="pill-button" id="plus-five" type="button">＋5 分鐘</button>
          </div>
        </section>

        <section class="control-panel" aria-labelledby="settings-heading">
          <div class="panel-heading">
            <div><p class="eyebrow">控制台</p><h1 id="settings-heading">設定計時器</h1></div>
            <span class="shortcut-hint"><kbd>Space</kbd> 暫停／繼續</span>
          </div>

          <div class="time-inputs" aria-label="計時長度">
            <label><span>分鐘</span><input id="minutes" type="number" inputmode="numeric" min="0" max="999" step="1" value="${settings.minutes}" autocomplete="off"><small>0–999</small></label>
            <span class="time-separator" aria-hidden="true">:</span>
            <label><span>秒數</span><input id="seconds" type="number" inputmode="numeric" min="0" max="59" step="1" value="${settings.seconds}" autocomplete="off"><small>0–59</small></label>
          </div>
          <p class="field-error" id="input-error" role="alert"></p>

          <div class="primary-actions">
            <button class="button button-primary" id="start" type="button">
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="m7 4 9 6-9 6V4Z"/></svg>
              開始計時
            </button>
            <button class="button button-pause" id="pause" type="button" disabled>
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6 4h3v12H6zm5 0h3v12h-3z"/></svg>
              暫停
            </button>
            <button class="button button-secondary" id="reset" type="button" disabled>
              <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M5.3 6.1A6 6 0 1 1 4 10M4 4v6h6"/></svg>
              重設
            </button>
          </div>

          <div class="divider"></div>

          <div class="preference-list">
            <label class="preference-row">
              <span><strong>歸零提示音</strong><small>時間歸零時播放一次柔和提示</small></span>
              <input class="switch-input" id="sound-toggle" type="checkbox" ${settings.soundEnabled ? 'checked' : ''}><span class="switch" aria-hidden="true"></span>
            </label>
            <label class="preference-row">
              <span><strong>平順色彩轉換</strong><small>依剩餘比例切換綠、黃、紅提示</small></span>
              <input class="switch-input" id="animation-toggle" type="checkbox" ${settings.animationsEnabled ? 'checked' : ''}><span class="switch" aria-hidden="true"></span>
            </label>
          </div>

          <button class="button button-pip" id="open-pip" type="button" ${pipSupported ? '' : 'disabled'}>
            <svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3 4h14v12H3V4Zm7 5h5v4h-5V9Z"/></svg>
            開啟置頂計時器
          </button>
          <p class="pip-message ${pipSupported ? '' : 'is-warning'}" id="pip-message">${pipSupported
            ? '開始計時時會自動開啟純顯示視窗。雙擊視窗可顯示固定控制列。'
            : '此瀏覽器不支援 Document Picture-in-Picture。請改用桌面版 Chrome 或 Edge；目前仍可使用頁內計時。'}</p>
        </section>
      </main>

      <footer>
        <p>專為全螢幕簡報與整個顯示器分享設計</p>
        <div class="shortcut-list" aria-label="鍵盤快捷鍵"><span><kbd>R</kbd> 重設</span><span><kbd>1</kbd> ＋1 分鐘</span><span><kbd>5</kbd> ＋5 分鐘</span><span><kbd>F</kbd> 置頂視窗</span></div>
      </footer>
    </div>
  `;

  return {
    minutes: query(root, '#minutes'),
    seconds: query(root, '#seconds'),
    start: query(root, '#start'),
    pause: query(root, '#pause'),
    reset: query(root, '#reset'),
    plusOne: query(root, '#plus-one'),
    plusFive: query(root, '#plus-five'),
    openPip: query(root, '#open-pip'),
    sound: query(root, '#sound-toggle'),
    animations: query(root, '#animation-toggle'),
    timer: query(root, '#main-timer'),
    phase: query(root, '#phase-label'),
    browserStatus: query(root, '#browser-status'),
    pipStatus: query(root, '#pip-status'),
    inputError: query(root, '#input-error'),
    pipMessage: query(root, '#pip-message'),
  };
}

export function updateMainView(elements: MainElements, snapshot: TimerSnapshot, settings: UserSettings, pipOpen: boolean): void {
  elements.timer.textContent = formatTimer(snapshot);
  elements.timer.dataset.tone = getTimerTone(snapshot, settings.thresholds);
  elements.timer.classList.toggle('no-transition', !settings.animationsEnabled);

  const phaseLabels = { idle: '尚未開始', running: snapshot.isOvertime ? '超時中' : '計時中', paused: '已暫停' } as const;
  elements.phase.textContent = phaseLabels[snapshot.phase];
  elements.phase.dataset.phase = snapshot.isOvertime ? 'overtime' : snapshot.phase;
  elements.pause.disabled = snapshot.phase === 'idle';
  elements.reset.disabled = snapshot.phase === 'idle';
  elements.start.disabled = snapshot.phase !== 'idle';
  elements.pause.innerHTML = snapshot.phase === 'paused'
    ? '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="m7 4 9 6-9 6V4Z"/></svg>繼續'
    : '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6 4h3v12H6zm5 0h3v12h-3z"/></svg>暫停';
  elements.openPip.textContent = pipOpen ? '置頂視窗已開啟' : snapshot.phase === 'idle' ? '開啟置頂計時器' : '重新開啟置頂視窗';
  elements.openPip.disabled = pipOpen || !Boolean(window.documentPictureInPicture);
  elements.pipStatus.innerHTML = `<span class="status-dot"></span>${pipOpen ? '置頂視窗已開啟' : '置頂視窗未開啟'}`;
  elements.pipStatus.classList.toggle('status-ok', pipOpen);
}

function query<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (!element) throw new Error(`Missing UI element: ${selector}`);
  return element;
}
